import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Layout from "./Components/Layout";
import GuestRoute from "./Components/GuestRoute";
import ProtectedRoute from "./Components/ProtectedRoute";

import Home from "./Components/Home";
import JobListings from "./Components/Jobs/JobListings";
import JobDetails from "./Components/Jobs/JobDetails";
import ApplyJob from "./Components/Jobs/ApplyJob";
import About from "./Components/About";
import Contact from "./Components/Contact";
import Login from "./Components/Login";
import Signup from "./Components/Signup";
import ForgotPassword from "./Components/ForgotPassword";
import ResetPassword from "./Components/ResetPassword";
import EmailVerified from "./Components/EmailVerified";
import Pricing from "./Components/Pricing";
import CandidateDashboard from "./Components/Candidates/CandidateDashboard";
import CandidateProfileSettings from "./Components/Candidates/CandidateProfileSettings";
import CandidateViewProfile from "./Components/Candidates/CandidateViewProfile";
import CandidateAppliedJobs from "./Components/Candidates/CandidateAppliedJobs";
import CandidateShortlistedJobs from "./Components/Candidates/CandidateShortlistedJobs";
import CandidateJobAlerts from "./Components/Candidates/CandidateJobAlerts";
import PublicCompanies from "./Components/Companies/PublicCompanies";
import PublicCompanyDetails from "./Components/Companies/PublicCompanyDetails";
import EmployeeDashboard from "./Components/Employees/EmployeeDashboard";
import EmployeeCompanyProfile from "./Components/Employees/EmployeeCompanyProfile";
import EmployeeViewCompanyProfile from "./Components/Employees/EmployeeViewCompanyProfile";
import EmployeePostJob from "./Components/Employees/EmployeePostJob";
import EmployeeManageJobs from "./Components/Employees/EmployeeManageJobs";
import EmployeeApplicants from "./Components/Employees/EmployeeApplicants";
import EmployeePlans from "./Components/Employees/EmployeePlans";
import AdminDashboard from "./Components/Admin/AdminDashboard";
import AdminJobs from "./Components/Admin/AdminJobs";
import AdminEmployers from "./Components/Admin/AdminEmployers";
import AdminCandidates from "./Components/Admin/AdminCandidates";
import AdminApplications from "./Components/Admin/AdminApplications";
import AdminContactMessages from "./Components/Admin/AdminContactMessages";
import AdminAuditLogs from "./Components/Admin/AdminAuditLogs";
import AdminPayments from "./Components/Admin/AdminPayments";
import AdminBillingCustomers from "./Components/Admin/AdminBillingCustomers";
import AdminEmployerDetails from "./Components/Admin/AdminEmployerDetails";
import AdminCandidateDetails from "./Components/Admin/AdminCandidateDetails";
import AdminSettings from "./Components/Admin/AdminSettings";
import AdminProfile from "./Components/Admin/AdminProfile";
import EmployeeJobPreview from "./Components/Employees/EmployeeJobPreview";
import PaymentSuccess from "./Components/PaymentSuccess";
import PaymentFailed from "./Components/PaymentFailed";
import Terms from "./Components/Terms";
import Privacy from "./Components/Privacy";
import Invoice from "./Components/Invoice";
import NotFound from "./Components/NotFound";

function App() {
  return (
    <BrowserRouter>
      <Routes>

        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/jobs" element={<JobListings />} />
          <Route path="/jobs/:id" element={<JobDetails />} />
          <Route path="/companies" element={<PublicCompanies />} />
          <Route path="/companies/:id" element={<PublicCompanyDetails />} />
          <Route path="/candidates" element={<Navigate to="/companies" replace />} />
          <Route path="/candidates/:id" element={<Navigate to="/companies" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>
          <Route path="/email-verified" element={<EmailVerified />} />

          <Route element={<ProtectedRoute role="seeker" />}>
            <Route path="/jobs/:id/apply" element={<ApplyJob />} />
            <Route path="/candidate-dashboard" element={<CandidateDashboard />} />
            <Route path="/candidate-profile" element={<CandidateProfileSettings />} />
            <Route path="/candidate-view-profile" element={<CandidateViewProfile />} />
            <Route path="/candidate-applied-jobs" element={<CandidateAppliedJobs />} />
            <Route path="/candidate-shortlisted-jobs" element={<CandidateShortlistedJobs />} />
            <Route path="/candidate-job-alerts" element={<CandidateJobAlerts />} />
          </Route>

          <Route element={<ProtectedRoute role="employer" />}>
            <Route path="/employee-dashboard" element={<EmployeeDashboard />} />
            <Route path="/employee-company-profile" element={<EmployeeCompanyProfile />} />
            <Route path="/employee-view-company-profile" element={<EmployeeViewCompanyProfile />} />
            <Route path="/employee-post-job" element={<EmployeePostJob />} />
            <Route path="/employee-edit-job/:id" element={<EmployeePostJob />} />
            <Route path="/employee-job-preview" element={<EmployeeJobPreview />} />
            <Route path="/employee-manage-jobs" element={<EmployeeManageJobs />} />
            <Route path="/employee-applicants" element={<EmployeeApplicants />} />
            <Route path="/employee-applicants/:jobId" element={<EmployeeApplicants />} />
            <Route path="/employee-plans" element={<EmployeePlans />} />
          </Route>
          <Route element={<ProtectedRoute role="admin" />}>
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/admin-jobs" element={<AdminJobs />} />
            <Route path="/admin-post-job" element={<EmployeePostJob />} />
            <Route path="/admin-edit-job/:id" element={<EmployeePostJob />} />
            <Route path="/admin-jobs/:jobId/applicants" element={<AdminApplications />} />
            <Route path="/admin-employers" element={<AdminEmployers />} />
            <Route path="/admin-employers/:employerId" element={<AdminEmployerDetails />} />
            <Route path="/admin-candidates" element={<AdminCandidates />} />
            <Route path="/admin-candidates/:candidateId" element={<AdminCandidateDetails />} />
            <Route path="/admin-applications" element={<AdminApplications />} />
            <Route path="/admin-audit-logs" element={<AdminAuditLogs />} />
            <Route path="/admin-contact-messages" element={<AdminContactMessages />} />
            <Route path="/admin-payments" element={<AdminPayments />} />
            <Route path="/admin-billing-customers" element={<AdminBillingCustomers />} />
            <Route path="/admin-settings" element={<AdminSettings />} />
            <Route path="/admin-profile" element={<AdminProfile />} />
          </Route>
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route element={<ProtectedRoute roles={["employer", "admin"]} />}>
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failed" element={<PaymentFailed />} />
            <Route path="/invoice" element={<Invoice />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>

      </Routes>
    </BrowserRouter>
  );
}

export default App;
